import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState(searchParams.get('token') || '');
    const [resendEmail, setResendEmail] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const { verifyEmail, resendVerification, user } = useAuth();
    const navigate = useNavigate();

    // Auto-verify if token is in URL
    useEffect(() => {
        const urlToken = searchParams.get('token');
        if (urlToken) {
            handleVerify(urlToken);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleVerify = async (verificationToken) => {
        setError('');
        setSuccess('');
        setLoading(true);

        const tokenToVerify = verificationToken || token;
        if (!tokenToVerify) {
            setError('Please enter a verification token');
            setLoading(false);
            return;
        }

        const result = await verifyEmail(tokenToVerify);

        if (result.ok) {
            setSuccess('Email verified successfully! Redirecting to plan selection...');
            setTimeout(() => {
                navigate('/select-plan');
            }, 2000);
        } else if (result.status === 400) {
            setError('Invalid or expired verification token');
        } else {
            setError('Verification failed. Please try again.');
        }

        setLoading(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleVerify();
    };

    const handleResend = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setResendLoading(true);

        if (!resendEmail) {
            setError('Please enter your email address');
            setResendLoading(false);
            return;
        }

        const result = await resendVerification(resendEmail);

        if (result.ok) {
            setSuccess('Verification email sent! Please check your inbox.');
        } else if (result.status === 400) {
            setError('Email already verified or user not found');
        } else if (result.status === 404) {
            setError('User not found. Please check your email address.');
        } else {
            setError('Failed to send verification email. Please try again.');
        }

        setResendLoading(false);
    };

    return (
        <div className="bg-dark text-light d-flex align-items-center" style={{ minHeight: '100vh' }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-sm-10 col-md-8 col-lg-6">
                        <div className="card bg-secondary-subtle text-light shadow-lg">
                            <div className="card-body">
                                <h3 className="mb-3">Verify Your Email</h3>

                                {success && (
                                    <div className="alert alert-success" role="alert">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        {success}
                                    </div>
                                )}

                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {error}
                                    </div>
                                )}

                                {!success && !searchParams.get('token') && (
                                    <div className="alert alert-info">
                                        <p className="mb-0">
                                            <i className="bi bi-info-circle-fill me-2"></i>
                                            Please check your email and click the verification link, or enter the verification token below.
                                        </p>
                                    </div>
                                )}

                                {/* Verification Form */}
                                <form onSubmit={handleSubmit} className="mb-4">
                                    <div className="mb-3">
                                        <label className="form-label">Verification Token</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={token}
                                            onChange={(e) => setToken(e.target.value)}
                                            placeholder="Enter verification token"
                                            disabled={loading}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary w-100"
                                        type="submit"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Verifying...
                                            </>
                                        ) : (
                                            'Verify Email'
                                        )}
                                    </button>
                                </form>

                                {/* Resend Section */}
                                <div className="border-top pt-3">
                                    <p className="small mb-2">Didn't receive the email?</p>
                                    <form onSubmit={handleResend}>
                                        <div className="mb-3">
                                            <label className="form-label">Email Address</label>
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={resendEmail}
                                                onChange={(e) => setResendEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                disabled={resendLoading}
                                            />
                                        </div>
                                        <button
                                            className="btn btn-outline-primary w-100"
                                            type="submit"
                                            disabled={resendLoading}
                                        >
                                            {resendLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Sending...
                                                </>
                                            ) : (
                                                'Resend Verification'
                                            )}
                                        </button>
                                    </form>
                                </div>

                                <p className="mt-3 small mb-0">
                                    <Link to="/login">Back to Login</Link>
                                </p>
                            </div>
                        </div>

                        <p className="text-center mt-3">
                            <a className="link-light" href="/">← Back to site</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;

